import { bodyCopyMedium, Col4Col8, ColFull, RadioButton, Select } from '@aon/aon-tws-common-ui';
import { IRadioOption } from '@aon/aon-tws-common-ui/build/lib/components/Common/RadioButton';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ContentConstants from '../../../../contentConstants';
import { IPageFlowElementModel, ISurveyQuestionEditModel } from '../../../../models';
import { PageFlowElementModalUtil } from '../../../../utilities/PageFlowElementModalUtil';
import { LabelMedium } from '../../../common/styles/globalStyles';
import { PageFlowQuestionSection } from '../PageFlowQuestionSection';
import { IPageFlowElementModalProps, PageFlowElementModal } from './PageFlowElementModal';

const LabelMediumContainer = styled(LabelMedium)`
	display: inline;
	margin-left: 16px;
`;
LabelMediumContainer.displayName = 'LabelMediumContainer';

const RadioContainer = styled.div.attrs<{}>({
	className: 'tws-radio-series',
})`
	&.tws-radio-series {
		.tws-input-radio-label {
			font-size: 13.5px;
			font-weight: 700;
			color: #444444;
			line-height: 1.4em;
		}
	}
`;
RadioContainer.displayName = 'RadioContainer';

const NoBulkText = styled.div`
	${bodyCopyMedium};
`;
NoBulkText.displayName = 'NoBulkText';

// Comment to satisfy SonarQube's petty need to throw an error and make the PR build fail when you have only test files checked in. This comment can be removed if you are reading this.
export const PageBreakModal = (props: IPageFlowElementModalProps) => {
	const title = props.pageFlowElement
		? ContentConstants.LOGIC_AND_LAYOUT.EDIT_PAGE_BREAK
		: ContentConstants.LOGIC_AND_LAYOUT.ADD_PAGE_BREAK;

	const InitializeRadios = () => {
		const radiosOptions: IRadioOption[] = [
			{
				name: 'beforeQuestions',
				displayText: ContentConstants.LOGIC_AND_LAYOUT.BEFORE_QUESTION,
				checked: true,
			},
			{
				name: 'afterEvery',
				displayText: ContentConstants.LOGIC_AND_LAYOUT.AFTER_EVERY,
				checked: false,
			},
		];
		return radiosOptions;
	};

	const InitializeIncrementList = () => {
		let counter = 1;
		const halfQuestions = _.round(props.questions.length / 2);
		let maxSlice = halfQuestions > 1 ? halfQuestions - 2 : 0;
		maxSlice = Math.min(50, maxSlice);
		return props.questions.slice(0, maxSlice).map((q: ISurveyQuestionEditModel) => {
			counter++;
			return { name: counter.toString(), displayText: counter, displayOrder: counter };
		});
	};

	useEffect(() => {
		setIsBeforeQuestions(true);
		PageFlowElementModalUtil.HandleEffect(props, setQuestionId, setQuestionError, setSurveyElement);
		return setDefaultState;
	}, [props.pageFlowElement, props.questions, props.maxId, props.isOpen]);

	const [questionId, setQuestionId] = useState('');
	const [surveyElement, setSurveyElement] = useState<IPageFlowElementModel>(PageFlowElementModalUtil.InitializeSurveyElement(props));
	const [questionError, setQuestionError] = useState(false);
	const [questionList, setQuestionList] = useState(PageFlowElementModalUtil.InitializeQuestionList(props, true));
	const [incrementList, setIncrementList] = useState(InitializeIncrementList());
	const [isBeforeQuestions, setIsBeforeQuestions] = useState(true);
	const [radios, setRadios] = useState(InitializeRadios());
	const [increment, setIncrement] = useState(2);
	const [afterEveryDisabled, setAfterEveryDisabled] = useState(props.questions.length < 5);

	const setDefaultState = () => {
		setRadios(InitializeRadios());
		setIsBeforeQuestions(true);
		setIncrement(2);
		setIncrementList(InitializeIncrementList());
		setAfterEveryDisabled(props.questions.length < 5);
		PageFlowElementModalUtil.SetDefaultState(props, setQuestionId, setQuestionError, setSurveyElement, setQuestionList, true);
	};

	const toggleRadio = (value: boolean) => {
		setIsBeforeQuestions(value);
	};

	const HandleIncrementChange = (value: any) => {
		setIncrement(value);
	};

	const handleSubmit = () => {
		return PageFlowElementModalUtil.HandleSubmit(
			props,
			questionId,
			surveyElement,
			setQuestionError,
			setSurveyElement,
			PageFlowElementModalUtil.IsValid,
			isBeforeQuestions ? undefined : increment,
		);
	};

	const handleClose = () => {
		PageFlowElementModalUtil.HandleClose(setDefaultState, props.onClose);
	};

	return (
		<PageFlowElementModal
			{...props}
			onSubmit={handleSubmit}
			onClose={handleClose}
			title={title}
			contentFullWidth={false}
			modalType={'lightbox'}
		>
			<>
				<Col4Col8
					subLabelDisplayData={
						<RadioContainer>
							<RadioButton
								groupName={'pageBreak'}
								isChecked={isBeforeQuestions}
								key={'beforeQuestions'}
								item={radios[0]}
								handleChange={(value: boolean) => toggleRadio(true)}
								showTooltips={true}
							/>
						</RadioContainer>
					}
					className='tws-question-row'
				>
					<PageFlowQuestionSection
						questions={questionList}
						questionError={questionError}
						questionId={questionId}
						handleQuestionChange={(value: any) => PageFlowElementModalUtil.HandleQuestionChange(value, setQuestionId, setQuestionError)}
						disabled={!isBeforeQuestions}
					/>
				</Col4Col8>
				{!props.pageFlowElement && (
					<>
						<Col4Col8
							subLabelDisplayData={
								<RadioContainer>
									<RadioButton
										groupName={'pageBreak'}
										isChecked={!isBeforeQuestions}
										key={'afterEvery'}
										item={radios[1]}
										handleChange={(value: boolean) => toggleRadio(afterEveryDisabled)}
										showTooltips={true}
										disabled={afterEveryDisabled}
									/>
								</RadioContainer>
							}
							className='tws-afterevery-row'
						>
							<Select
								items={incrementList}
								selectWidth='70px'
								onApply={(value: any) => HandleIncrementChange(value)}
								displayText={ContentConstants.LOGIC_AND_LAYOUT.SELECT_INCREMENT}
								selectedValue={increment.toString()}
								disabled={isBeforeQuestions || afterEveryDisabled}
							/>
							<LabelMediumContainer>{ContentConstants.LOGIC_AND_LAYOUT.QUESTIONS}</LabelMediumContainer>
						</Col4Col8>
						{afterEveryDisabled && (
							<ColFull>
								<NoBulkText>Adding page breaks in bulk is disabled because you have less than 5 questions.</NoBulkText>
							</ColFull>
						)}
					</>
				)}
			</>
		</PageFlowElementModal>
	);
};
